import {
  GENERATION_DISCLAIMER,
  splitGenerationDisclaimer,
} from '@util/fitment/generationNotice';
import { MXLockerLogoSmall } from '@c/icons/MXLockerLogoSmall';
import { Sparkles } from 'lucide-react';
import { ProductDocument } from 'models/product';
import SellerFitmentIcon from './SellerFitmentIcon';

type FitmentSourceBannerProps = {
  product: ProductDocument;
};

export default function FitmentSourceBanner({
  product,
}: FitmentSourceBannerProps) {
  // Only show banner if there's fitment data
  const hasFitmentData =
    product.fitting_bikes?.bikes && product.fitting_bikes.bikes.length > 0;

  const hasAIDisclaimer =
    !!product.disclaimer &&
    product.disclaimer.toLowerCase().includes('ai-assisted');

  if (splitGenerationDisclaimer(product.disclaimer).hasGenerationDisclaimer) {
    return (
      <div className="flex items-center gap-4 rounded-2xl bg-slate-100 p-4">
        <MXLockerLogoSmall className="h-8 w-8 shrink-0" fill="#000" />
        <div className="space-y-3 text-[1.6rem] font-medium text-black">
          <p>{GENERATION_DISCLAIMER}</p>
          {hasAIDisclaimer && (
            <p>
              MX Locker AI-assisted fitment based on title and description.
              Confirm full compatibility with the seller before purchasing.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!hasFitmentData) {
    return null;
  }

  // Determine fitment source
  // AI-assisted: Product has fitting_bikes_strings (AI-extracted from title/description)
  // OR disclaimer contains "AI-assisted" text
  // This takes priority over guaranteed check

  // Check if this is an MXLockerDirect listing
  const isMXLockerDirect =
    'seller_id' in product &&
    product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12';

  // Guaranteed: MXLockerDirect listing OR product has sources with data_depot collection
  // (from MX Locker's verified database)
  // Only if not AI-assisted
  const isGuaranteed =
    !hasAIDisclaimer &&
    (isMXLockerDirect ||
      (!!product.sources &&
        product.sources.some((source) => source.collection === 'data_depot')));

  // Seller-provided: Default case
  const isSellerProvided = !isGuaranteed && !hasAIDisclaimer;

  if (isGuaranteed) {
    return (
      <div className="flex items-center gap-4 rounded-2xl bg-slate-100 p-4">
        <MXLockerLogoSmall className="h-8 w-8 shrink-0" fill="#000" />
        <span className="text-[1.6rem] font-medium text-black">
          MX Locker Guaranteed Fit — fitment verified for your exact vehicle, or
          return it free.
        </span>
      </div>
    );
  }

  if (hasAIDisclaimer) {
    return (
      <div className="flex items-center gap-4 rounded-2xl bg-[#F1F1F1] py-5 pl-4 pr-8">
        <Sparkles className="h-8 w-8 shrink-0 text-purple-600" />
        <span className="text-[1.6rem] font-medium text-black">
          MX Locker AI-assisted fitment based on title and description. Confirm
          full compatibility with the seller before purchasing.
        </span>
      </div>
    );
  }

  if (isSellerProvided) {
    return (
      <div className="flex items-center gap-4 rounded-2xl bg-[#F1F1F1] p-4">
        <SellerFitmentIcon className="h-8 w-8 shrink-0" fill="#C41819" />
        <span className="text-[1.6rem] font-medium text-black">
          Fitment data provided by seller. Seller assumes all responsibility for
          this listing.
        </span>
      </div>
    );
  }

  return null;
}
